import TextAreaField from "@/components/shared/formFields/textarea";
import Button from "@/components/shared/ui/Button";
import useGetAdditionalInfo from "@/hooks/methodology/useGetAdditionalInfo";
import usePostAdditionalInfo from "@/hooks/methodology/usePostAdditionalInfo";
import { MethodologyAdditionalInfoResponse } from "@/types/AdditionalInfo";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import { Control, FieldValues, SubmitHandler, useController, useForm } from "react-hook-form";
import { BsPencil } from "react-icons/bs";

interface SummaryData {
	overview: string;
	applicability: string;
	projectBoundary: string;
	baselineScenario: string;
	additionality: string;
	quantification: string;
	monitoring: string;
	methodologyId: string;
}

type SummaryFieldType = {
	label: string;
	control: FieldValues;
	name: string;
	data: MethodologyAdditionalInfoResponse | undefined;
	canEditData?: boolean;
};

interface SummaryTabProps {
	data: MethodologyAdditionalInfoResponse | undefined;
	onSubmitSuccessFn: () => void;
	isOriginator?: boolean;
	roundStatus?: string;
}

const Summary = (props: SummaryTabProps) => {
	const { data, onSubmitSuccessFn } = props;
	const router = useRouter();
	const { query } = router;
	const methodologyId = query.id?.toString();
	const { data: methodologyData } = useGetAdditionalInfo(methodologyId);
	const { mutate: submitSummary, isLoading } = usePostAdditionalInfo("Summary", onSubmitSuccessFn);
	const { control, handleSubmit, reset } = useForm<SummaryData>();

	const labels = [
		{ label: "Overview", name: "overview" },
		{ label: "Applicability", name: "applicability" },
		{ label: "Project Boundary", name: "projectBoundary" },
		{ label: "Baseline Scenario", name: "baselineScenario" },
		{ label: "Demonstration of Additionality", name: "additionality" },
		{ label: "Quantification of Reduction/Removals", name: "quantification" },
		{ label: "Monitoring", name: "monitoring" },
	];

	const canEditData =
		data?.status === "CONDITIONALLY_APPROVED" &&
		props.isOriginator &&
		!(data?.methodologyAdditionalInfo?.status === "SUBMITTED");

	const handleCancel = () => {
		const methodologyAdditionalInfo = methodologyData?.methodologyAdditionalInfo;

		reset({
			overview: methodologyAdditionalInfo?.overview,
			applicability: methodologyAdditionalInfo?.applicability,
			projectBoundary: methodologyAdditionalInfo?.projectBoundary,
			baselineScenario: methodologyAdditionalInfo?.baselineScenario,
			additionality: methodologyAdditionalInfo?.additionality,
			quantification: methodologyAdditionalInfo?.quantification,
			monitoring: methodologyAdditionalInfo?.monitoring,
		});
	};
	const onSubmit: SubmitHandler<SummaryData> = (values: SummaryData) => {
		if (!values) return;

		const formData = new FormData();
		if (data?.methodologyAdditionalInfo?.id) {
			const methodologyAdditionalInfoId = data?.methodologyAdditionalInfo?.id.toString();
			formData.append("methodologyAdditionalInfoId", methodologyAdditionalInfoId!!);
		}
		formData.append("methodologyId", methodologyId!!);

		for (let [key, value] of Object.entries(values)) {
			if (value !== undefined) {
				formData.append(key, value);
			}
		}

		submitSummary(formData);
	};

	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			{labels.map((label, index) => (
				<SummaryField
					key={index}
					label={label.label}
					name={label.name}
					control={control}
					data={data}
					canEditData={canEditData}
				/>
			))}
			{canEditData && (
				<div className="mt-5 flex items-center space-x-3">
					<Button onClick={handleCancel} variant="outline">
						Cancel
					</Button>
					<Button disabled={isLoading} type="submit" variant="solid">
						Save Changes
					</Button>
				</div>
			)}
		</form>
	);
};

const SummaryField = ({ label, control, name, data, canEditData }: SummaryFieldType) => {
	const additionalInfo = data?.methodologyAdditionalInfo;

	const getDefaultValues = (label: string) => {
		switch (label) {
			case "Overview":
				return additionalInfo?.overview || "";
			case "Applicability":
				return additionalInfo?.applicability || "";
			case "Project Boundary":
				return additionalInfo?.projectBoundary || "";
			case "Baseline Scenario":
				return additionalInfo?.baselineScenario || "";
			case "Demonstration of Additionality":
				return additionalInfo?.additionality || "";
			case "Quantification of Reduction/Removals":
				return additionalInfo?.quantification || "";
			case "Monitoring":
				return additionalInfo?.monitoring || "";
		}
	};

	const {
		field: { value, onChange },
	} = useController({
		name: name,
		control: control as Control,
		defaultValue: getDefaultValues(label),
	});
	const textAreaRef = useRef<HTMLTextAreaElement | null>(null);
	const [isInputVisible, setIsInputVisible] = useState(false);

	useEffect(() => {
		if (isInputVisible && textAreaRef.current) {
			textAreaRef.current.focus();
		}
	}, [isInputVisible]);
	const handleLabelClick = () => {
		setIsInputVisible((prevState) => !prevState);
	};

	const handleBlur = () => {
		setTimeout(() => {
			setIsInputVisible(false);
		}, 300);
	};

	return (
		<div className="mt-5 flex w-full flex-col space-y-5 lg:w-1/2">
			<div className="space-x-3">
				<span className="text-xl font-bold ">{label}</span>
				{canEditData && (
					<Button onClick={handleLabelClick} variant="transparent" className="bg-white px-2 text-gray-500">
						<BsPencil />
					</Button>
				)}
			</div>

			{isInputVisible ? (
				<TextAreaField rows={4} {...{ value, onChange, onBlur: handleBlur, ref: textAreaRef }} />
			) : (
				<p className="text-md">{value}</p>
			)}
		</div>
	);
};

export default Summary;
